import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase';
import { useAuth } from '../../contexts/AuthContext';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';

interface Question {
  id: string;
  questionId?: string;
  index?: number;
  Question: string;
  Answer01: string;
  Answer02: string;
  Answer03: string;
  CollectAnswer: string;
  Chapter?: number;
  Qno?: number;
}

export default function ExamPage() {
  const navigate = useNavigate();
  const { userId } = useAuth(); // Get userId from AuthContext
  const [currentPage, setCurrentPage] = useState(1);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({}); // key: question index, value: option index (0, 1, 2)

  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [testId, setTestId] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [canStartExam, setCanStartExam] = useState(false);
  const [resolvedExamSetId, setResolvedExamSetId] = useState<string | null>(null);
  const { settings, loading: restrictionsLoading } = useLearningRestrictions();
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  useEffect(() => {
    const storedExam = localStorage.getItem('selectedExam');
    if (storedExam) {
      const exam = JSON.parse(storedExam);
      setTestId(exam.id);
      if (exam.orderId) setOrderId(exam.orderId);
    } else {
      // If no exam selected, redirect to select-exam
      navigate('/select-exam');
    }
  }, [navigate]);

  useEffect(() => {
    if (!restrictionsLoading && !settings.examEnabled) {
      setIsRestrictionOpen(true);
    }
  }, [restrictionsLoading, settings.examEnabled]);

  useEffect(() => {
    const verifyEligibility = async () => {
      if (restrictionsLoading) return;
      if (!settings.examEnabled) {
        setCheckingEligibility(false);
        return;
      }
      if (!orderId || !userId) {
        setCanStartExam(true);
        setCheckingEligibility(false);
        return;
      }

      try {
        const orderRef = doc(db, 'Order', orderId);
        const orderSnap = await getDoc(orderRef);

        if (!orderSnap.exists()) {
          alert('受験枠が見つかりませんでした。');
          navigate('/select-exam');
          return;
        }

        const orderData = orderSnap.data() as any;
        const userRef = orderData.UserRef;

        if (!userRef || userRef.id !== userId) {
          alert('この試験を受験する権限がありません。');
          navigate('/home');
          return;
        }

        const retest1 = orderData.retest1 || false;
        const retest2 = orderData.retest2 || false;
        const retestAvailable = retest1 || retest2;

        let answerSheetExists = false;
        if (!retestAvailable && orderData.shikakuId) {
          const answerSheetQuery = query(
            collection(db, 'AnswerSheet'),
            where('UserRef', '==', userRef),
            where('ShikakuRef', '==', orderData.shikakuId)
          );
          const answerSheetSnapshot = await getDocs(answerSheetQuery);
          answerSheetExists = !answerSheetSnapshot.empty;
        }

        const alreadyCompleted = !retestAvailable && (orderData.isExamFinished || answerSheetExists);
        if (alreadyCompleted) {
          alert('この試験はすでに受験済みです。');
          navigate('/home');
          return;
        }

        if (retestAvailable) {
          const consumeRetestFlag = httpsCallable(functions, 'consumeRetestFlag');
          await consumeRetestFlag({ orderId });
        }

        setCanStartExam(true);
      } catch (error) {
        console.error('Error verifying exam eligibility: ', error);
        alert('受験枠の確認に失敗しました。');
        navigate('/home');
      } finally {
        setCheckingEligibility(false);
      }
    };

    verifyEligibility();
  }, [orderId, userId, navigate, restrictionsLoading, settings.examEnabled]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (restrictionsLoading || !settings.examEnabled) {
        setLoading(false);
        return;
      }
      if (!testId) return;

      try {
        console.log("Fetching questions for testId:", testId);
        let questionsData: Question[] = [];
        let usedExamSetId: string | null = null;

        if (orderId) {
          const orderSnap = await getDoc(doc(db, 'Order', orderId));
          if (orderSnap.exists()) {
            const orderData = orderSnap.data() as any;
            let examSetRef = orderData.examSetRef;

            if (!examSetRef && orderData.shikakuId) {
              const v0Query = query(
                collection(db, 'ExamSets'),
                where('shikakuRef', '==', orderData.shikakuId),
                where('version', '==', 0)
              );
              const v0Snapshot = await getDocs(v0Query);
              if (!v0Snapshot.empty) {
                examSetRef = v0Snapshot.docs[0].ref;
              }
            }

            if (examSetRef) {
              const examSetSnap = await getDoc(examSetRef);
              if (examSetSnap.exists()) {
                const examSetData = examSetSnap.data() as any;
                const setQuestions = Array.isArray(examSetData.questions) ? examSetData.questions : [];
                usedExamSetId = examSetSnap.id;
                questionsData = setQuestions
                  .map((q: any, idx: number) => ({
                    id: q.questionId || `${examSetSnap.id}-${q.index || idx + 1}`,
                    questionId: q.questionId,
                    index: q.index || idx + 1,
                    Question: q.Question || '',
                    Answer01: q.Answer01 || '',
                    Answer02: q.Answer02 || '',
                    Answer03: q.Answer03 || '',
                    CollectAnswer: q.CollectAnswer || '',
                    Chapter: q.Chapter,
                    Qno: q.Qno,
                  }))
                  .sort((a, b) => (a.index || 0) - (b.index || 0));
              }
            }
          }
        }

        if (questionsData.length === 0) {
          const allQuestionsSnapshot = await getDocs(collection(db, 'Questions'));

          questionsData = allQuestionsSnapshot.docs
            .filter(doc => {
              const data = doc.data();
              const skrefId = data.SKref?.id || data.SKref;
              return skrefId === testId;
            })
            .map(doc => ({
              id: doc.id,
              questionId: doc.id,
              ...doc.data()
            } as Question))
            .sort((a, b) => {
              if ((a.Chapter || 0) !== (b.Chapter || 0)) {
                return (a.Chapter || 0) - (b.Chapter || 0);
              }
              return (a.Qno || 0) - (b.Qno || 0);
            });
        }

        setQuestions(questionsData);
        setResolvedExamSetId(usedExamSetId);
      } catch (error) {
        console.error("Error fetching questions: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [testId, orderId, restrictionsLoading, settings.examEnabled]);

  const questionsPerPage = 10;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = questions.slice(startIndex, endIndex);

  const answeredCount = Object.keys(answers).length;
  const unansweredCount = questions.length - answeredCount;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handleAnswer = (questionIndex: number, optionIndex: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleShowSubmitForm = () => {
    setShowSubmitForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      alert('ログインユーザー情報が取得できませんでした。再度ログインしてください。');
      return;
    }

    setIsSubmitting(true);

    // Prepare answers for Cloud Function
    // Cloud Function expects: { questionId, selectedAnswer }
    // We have answers as { questionIndex: optionIndex }
    // We need to map optionIndex back to the actual answer string
    const answersArray = Object.entries(answers).map(([indexStr, optionIdx]) => {
      const idx = parseInt(indexStr);
      const question = questions[idx];
      const options = [question.Answer01, question.Answer02, question.Answer03].filter(a => a);
      const selectedAnswer = options[optionIdx];
      return {
        questionId: question.questionId || question.id,
        questionIndex: question.index || idx + 1,
        selectedAnswer
      };
    });

    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      if (!projectId) {
        throw new Error('Firebase project ID is not configured.');
      }
      const response = await fetch(
        `https://asia-northeast1-${projectId}.cloudfunctions.net/submitTest`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            testId,
            orderId,
            examSetId: resolvedExamSetId,
            userAnswers: answersArray,
          }),
        }
      );

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert('送信中にエラーが発生しました。もう一度お試しください。');
      setIsSubmitting(false);
    }
  };


  if (restrictionsLoading) {
    return <div className="min-h-screen flex items-center justify-center">制限設定を確認中です...</div>;
  }

  if (!settings.examEnabled) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <RestrictionModal
          open
          message={settings.message}
          actionLabel="ホームへ戻る"
          onAction={() => navigate('/home')}
        />
      </div>
    );
  }

  if (checkingEligibility) {
    return <div className="min-h-screen flex items-center justify-center">受験枠を確認中です...</div>;
  }

  if (!canStartExam) {
    return null;
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-500">
                <i className="ri-file-list-3-line text-xl"></i>
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-800">資格試験</h1>
                <p className="text-xs text-gray-500">ページ {currentPage}/{totalPages}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/select-exam')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-6">
        <div className="space-y-6">
          {currentQuestions.map((question, idx) => {
            const questionIndex = startIndex + idx;
            const options = [question.Answer01, question.Answer02, question.Answer03].filter(a => a);

            return (
              <div
                key={question.id}
                className="bg-white rounded-2xl p-6 shadow-sm"
              >
                <div>
                  <h3 className="text-base font-bold text-gray-800 mb-4 leading-relaxed">
                    <span className="text-orange-500 mr-2">Q{questionIndex + 1}.</span>
                    {question.Question}
                  </h3>
                  <div className="space-y-3">
                    {options.map((option, optionIdx) => (
                      <button
                        key={optionIdx}
                        onClick={() => handleAnswer(questionIndex, optionIdx)}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 ${answers[questionIndex] === optionIdx
                          ? 'bg-orange-50 border-orange-200 shadow-sm'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 ${answers[questionIndex] === optionIdx
                            ? 'border-orange-500 bg-orange-500'
                            : 'border-gray-300'
                            }`}
                        >
                          {answers[questionIndex] === optionIdx && (
                            <i className="ri-check-line text-white text-xs"></i>
                          )}
                        </div>
                        <span className={`text-sm ${answers[questionIndex] === optionIdx ? 'text-orange-900 font-medium' : 'text-gray-700'}`}>
                          {option}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress Info */}
        <div className="mt-6 bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between text-xs mb-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-orange-400 rounded-full"></div>
                <span className="text-gray-600">回答済: <strong className="text-gray-800">{answeredCount}</strong></span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-gray-200 rounded-full"></div>
                <span className="text-gray-600">未回答: <strong className="text-gray-800">{unansweredCount}</strong></span>
              </div>
            </div>
            <div className="text-gray-400">
              {questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0}%
            </div>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-orange-400 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${questions.length > 0 ? (answeredCount / questions.length) * 100 : 0}%` }}
            ></div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex flex-col gap-4">
          <div className="flex justify-between gap-3">
            {currentPage > 1 ? (
              <button
                onClick={handlePrevPage}
                className="flex-1 px-4 py-3 bg-white text-gray-600 border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                前へ
              </button>
            ) : (
              <div className="flex-1"></div> /* Spacer */
            )}

            {currentPage < totalPages ? (
              <button
                onClick={handleNextPage}
                className="flex-1 px-4 py-3 bg-[#6B96C3] text-white rounded-xl font-bold hover:bg-[#5A85B2] transition-all whitespace-nowrap cursor-pointer text-sm shadow-sm"
              >
                次へ
                <i className="ri-arrow-right-line ml-1"></i>
              </button>
            ) : (
              <button
                onClick={handleShowSubmitForm}
                className="flex-1 px-4 py-3 bg-[#F4B183] text-white rounded-xl font-bold hover:bg-[#E5A072] transition-all whitespace-nowrap cursor-pointer text-sm shadow-sm"
              >
                <i className="ri-send-plane-fill mr-1"></i>
                提出
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Submit Form Modal */}
      {showSubmitForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl">
            {submitSuccess ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">送信完了</h3>
                <p className="text-sm text-gray-600">試験結果を送信しました</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="exam-result-form">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-send-line text-xl text-orange-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">試験結果の送信</h3>
                </div>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                  試験結果を送信します。<br />よろしいですか？
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowSubmitForm(false)}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer text-sm"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-[#F4B183] text-white rounded-xl font-bold hover:bg-[#E5A072] transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer text-sm shadow-sm"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <RestrictionModal
        open={isRestrictionOpen}
        message={settings.message}
        actionLabel="ホームへ戻る"
        onAction={() => navigate('/home')}
      />
    </div>
  );
}
